import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  FolderOpen,
  FileText,
  Plus,
  Search,
  Trash2,
  Eye,
  Link as LinkIcon,
  X,
  File,
  Image as ImageIcon,
  Film,
  Layers,
  ExternalLink,
  Pencil,
  LayoutGrid,
  List,
  FolderInput,
  Loader2,
  Youtube as YoutubeIcon,
  Music,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "../components/ConfirmModal";

// Tipos: orden Documento, Hoja de cálculo, Imagen, Video (incl. YouTube), Audio, Enlace.
const RESOURCE_TYPES = [
  { id: "drive_doc", label: "Documento", icon: FileText },
  { id: "drive_sheet", label: "Hoja de cálculo", icon: Layers },
  { id: "drive_pdf", label: "Documento", icon: FileText },
  { id: "image", label: "Imagen", icon: ImageIcon },
  { id: "video", label: "Video", icon: Film },
  { id: "youtube", label: "Video", icon: YoutubeIcon },
  { id: "audio", label: "Audio", icon: Music },
  { id: "link", label: "Enlace", icon: LinkIcon },
];

// Formulario crear/editar: Documento, Hoja de cálculo, Imagen, Video (YouTube se detecta por URL), Audio, Enlace.
const FORM_TYPES = [
  { id: "drive_doc", label: "Documento", icon: FileText },
  { id: "drive_sheet", label: "Hoja de cálculo", icon: Layers },
  { id: "image", label: "Imagen", icon: ImageIcon },
  { id: "video", label: "Video", icon: Film },
  { id: "audio", label: "Audio", icon: Music },
  { id: "link", label: "Enlace", icon: LinkIcon },
];

// Filtros (plural): Videos incluye video + YouTube; Audios reemplaza la tab YouTube.
const TYPE_OPTIONS_FILTER = [
  { id: "all", label: "Todos" },
  { id: "documentos", label: "Documentos" },
  { id: "drive_sheet", label: "Hojas de cálculo" },
  { id: "image", label: "Imágenes" },
  { id: "videos", label: "Videos" },
  { id: "audio", label: "Audios" },
  { id: "link", label: "Enlaces" },
];

function extractDriveFileId(url) {
  if (!url) return null;
  const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/) || url.match(/[-\w]{25,}/);
  return match ? (match[1] || match[0]) : null;
}

function extractYoutubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  return m ? m[1] : null;
}

function isDriveUrl(url) {
  return /drive\.google\.com|docs\.google\.com/i.test(url || "");
}

function isDirectVideoUrl(url) {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url) || /^data:video\//i.test(url);
}

function isDirectImageUrl(url) {
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url) || /^data:image\//i.test(url);
}

function isDirectAudioUrl(url) {
  if (!url) return false;
  return /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(url) || /^data:audio\//i.test(url);
}

/** Infiere tipo desde la URL (sin llamar al backend) para selección automática */
function inferTypeFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  if (extractYoutubeId(u)) return "youtube";
  if (isDirectImageUrl(u)) return "image";
  if (isDirectVideoUrl(u)) return "video";
  if (isDirectAudioUrl(u)) return "audio";
  if (isDriveUrl(u)) return null; // Drive se resuelve con el backend (Detectar)
  return null;
}

function parseTagsString(str) {
  if (!str || typeof str !== "string") return [];
  return str
    .split(/[,;]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Parsea created_at del backend como UTC para orden y visualización correctos (el API puede devolver fecha sin Z). */
function parseResourceDate(createdAt) {
  if (createdAt == null || createdAt === "") return null;
  const s = String(createdAt).trim();
  if (!s) return null;
  if (s.includes("T") && !/Z|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s + "Z");
  return new Date(s);
}

const LEGACY_TYPE_MAP = { document: "drive_doc", video: "video", link: "link" };

function isDriveType(type) {
  return ["drive_doc", "drive_sheet", "drive_pdf", "document"].includes(type);
}

function getTypeIcon(type) {
  const normalized = LEGACY_TYPE_MAP[type] || type;
  const t = RESOURCE_TYPES.find((r) => r.id === normalized);
  return t ? t.icon : File;
}

function getTypeLabel(type) {
  const normalized = LEGACY_TYPE_MAP[type] || type;
  if (normalized === "drive_doc" || normalized === "drive_pdf") return "Documentos";
  if (normalized === "youtube" || normalized === "video") return "Video";
  const t = RESOURCE_TYPES.find((r) => r.id === normalized);
  return t ? t.label : type;
}

const initialNewResource = {
  title: "",
  description: "",
  url: "",
  type: "drive_doc",
  tags: [],
  thumbnail: null,
};

const RecursosCompartidos = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [viewResource, setViewResource] = useState(null);
  const [editResource, setEditResource] = useState(null);
  const [newResource, setNewResource] = useState({ ...initialNewResource });
  const [tagsInput, setTagsInput] = useState("");
  const [editTagsInput, setEditTagsInput] = useState("");
  const [newTagInput, setNewTagInput] = useState("");
  const [editNewTagInput, setEditNewTagInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterTag, setFilterTag] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [viewMode, setViewMode] = useState("grid");
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveSearchQuery, setDriveSearchQuery] = useState("");
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  const fetchResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/resources?module=shared`, {
        withCredentials: true,
      });
      setResources(response.data || []);
    } catch (error) {
      console.error("Error fetching resources:", error);
      setResources([]);
      toast.error("Error al cargar recursos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Al abrir vista: obtener embedUrl para Drive, YouTube e imagen. Drive se muestra en iframe (puede aparecer aviso CSP en consola).
  useEffect(() => {
    if (!viewResource || !showViewModal) {
      setEmbedUrl(null);
      setEmbedLoading(false);
      return;
    }
    const type = viewResource.type;
    if (type === "youtube") {
      const vid = extractYoutubeId(viewResource.url);
      setEmbedUrl(vid ? `https://www.youtube.com/embed/${vid}` : null);
      setEmbedLoading(false);
      return;
    }
    if (type === "image") {
      if (isDriveUrl(viewResource.url)) {
        setEmbedLoading(true);
        setEmbedUrl(null);
        axios
          .post(`${API}/drive/embed-url`, { url: viewResource.url }, { withCredentials: true })
          .then((res) => setEmbedUrl(res.data?.embedUrl || null))
          .catch(() => {
            setEmbedUrl(null);
            toast.error("No se pudo cargar la imagen de Drive");
          })
          .finally(() => setEmbedLoading(false));
      } else {
        setEmbedUrl(viewResource.url);
        setEmbedLoading(false);
      }
      return;
    }
    if (type === "audio") {
      if (isDriveUrl(viewResource.url)) {
        setEmbedLoading(true);
        setEmbedUrl(null);
        axios
          .post(`${API}/drive/embed-url`, { url: viewResource.url }, { withCredentials: true })
          .then((res) => setEmbedUrl(res.data?.embedUrl || null))
          .catch(() => {
            setEmbedUrl(null);
            toast.error("No se pudo cargar el audio de Drive");
          })
          .finally(() => setEmbedLoading(false));
      } else {
        setEmbedUrl(viewResource.url);
        setEmbedLoading(false);
      }
      return;
    }
    if (type === "video") {
      if (isDriveUrl(viewResource.url)) {
        setEmbedLoading(true);
        setEmbedUrl(null);
        axios
          .post(
            `${API}/drive/embed-url`,
            { url: viewResource.url },
            { withCredentials: true }
          )
          .then((res) => {
            setEmbedUrl(res.data?.embedUrl || null);
          })
          .catch(() => {
            setEmbedUrl(null);
            toast.error("No se pudo obtener la vista previa del vídeo");
          })
          .finally(() => setEmbedLoading(false));
      } else {
        setEmbedUrl(viewResource.url);
        setEmbedLoading(false);
      }
      return;
    }
    if (isDriveType(type)) {
      setEmbedLoading(true);
      setEmbedUrl(null);
      axios
        .post(
          `${API}/drive/embed-url`,
          { url: viewResource.url },
          { withCredentials: true }
        )
        .then((res) => {
          setEmbedUrl(res.data?.embedUrl || null);
        })
        .catch(() => {
          setEmbedUrl(null);
          toast.error("No se pudo obtener la vista previa");
        })
        .finally(() => setEmbedLoading(false));
      return;
    }
    setEmbedUrl(null);
    setEmbedLoading(false);
  }, [viewResource, showViewModal]);

  const fetchDriveFiles = useCallback(async () => {
    setDriveLoading(true);
    try {
      const params = new URLSearchParams();
      if (driveSearchQuery.trim()) params.set("q", driveSearchQuery.trim());
      const res = await axios.get(`${API}/drive/files?${params}`, {
        withCredentials: true,
      });
      setDriveFiles(res.data?.files || []);
    } catch (e) {
      if (e.response?.status === 503) {
        toast.error("Google Drive no está configurado");
      } else {
        toast.error(e.response?.data?.detail || "Error al listar Drive");
      }
      setDriveFiles([]);
    } finally {
      setDriveLoading(false);
    }
  }, [driveSearchQuery]);

  useEffect(() => {
    if (showDrivePicker) fetchDriveFiles();
  }, [showDrivePicker, fetchDriveFiles]);

  const handlePasteUrl = useCallback(async () => {
    const url = newResource.url?.trim();
    if (!url) return;
    const inferred = inferTypeFromUrl(url);
    if (inferred) {
      setNewResource((prev) => ({ ...prev, type: inferred }));
      toast.success(`Tipo detectado: ${inferred === "youtube" ? "Video (YouTube)" : inferred === "image" ? "Imagen" : inferred === "audio" ? "Audio" : "Video"}`);
      return;
    }
    if (isDriveUrl(url)) {
      try {
        const res = await axios.post(
          `${API}/drive/embed-url`,
          { url },
          { withCredentials: true }
        );
        const data = res.data || {};
        setNewResource((prev) => ({
          ...prev,
          type: data.suggestedType || "drive_pdf",
          thumbnail: data.thumbnailLink || prev.thumbnail,
        }));
        toast.success("Tipo y vista previa de Drive detectados");
      } catch (e) {
        if (e.response?.status === 503) toast.error("Google Drive no configurado");
        else setNewResource((prev) => ({ ...prev, type: "drive_pdf" }));
      }
    }
  }, [newResource.url]);

  // Inferir tipo al cambiar URL (URLs directas imagen/video/youtube se seleccionan solas)
  const handleUrlChange = useCallback((value) => {
    const inferred = inferTypeFromUrl(value);
    setNewResource((prev) => ({ ...prev, url: value, ...(inferred ? { type: inferred } : {}) }));
  }, []);

  const handleAddResource = async () => {
    const tags = parseTagsString(tagsInput);
    const payload = {
      ...newResource,
      module: "shared",
      tags,
      description: newResource.description || undefined,
      thumbnail: newResource.thumbnail || undefined,
    };
    if (!payload.title?.trim() || !payload.url?.trim()) {
      toast.error("Título y URL son obligatorios");
      return;
    }
    try {
      const response = await axios.post(`${API}/resources`, payload, {
        withCredentials: true,
      });
      setResources((prev) => [response.data, ...prev]);
      setShowAddModal(false);
      setNewResource({ ...initialNewResource });
      setTagsInput("");
      setNewTagInput("");
      toast.success("Recurso agregado");
    } catch (error) {
      console.error("Error adding resource:", error);
      toast.error(error.response?.data?.detail || "Error al agregar recurso");
    }
  };

  const handleEditResource = async () => {
    if (!editResource?.resource_id) return;
    const tags = parseTagsString(editTagsInput);
    // Edición completa: enviar todos los campos para que el backend actualice el recurso por completo
    const payload = {
      title: editResource.title ?? "",
      description: editResource.description ?? "",
      url: editResource.url ?? "",
      type: editResource.type ?? "link",
      tags,
      thumbnail: editResource.thumbnail ?? "",
    };
    if (!payload.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    if (!payload.url.trim()) {
      toast.error("La URL es obligatoria");
      return;
    }
    try {
      const response = await axios.put(
        `${API}/resources/${editResource.resource_id}`,
        payload,
        { withCredentials: true }
      );
      setResources((prev) =>
        prev.map((r) => (r.resource_id === response.data.resource_id ? response.data : r))
      );
      setShowEditModal(false);
      setEditResource(null);
      setEditTagsInput("");
      setEditNewTagInput("");
      toast.success("Recurso actualizado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!resourceId) return;
    try {
      await axios.delete(`${API}/resources/${resourceId}`, { withCredentials: true });
      setResources((prev) => prev.filter((r) => r.resource_id !== resourceId));
      setDeleteConfirm(null);
      toast.success("Recurso eliminado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const openEdit = (resource) => {
    setEditResource({ ...resource });
    setEditTagsInput((resource.tags || []).join(", "));
    setShowEditModal(true);
  };

  const selectDriveFile = (file) => {
    const mime = file.mimeType || "";
    let type = "drive_pdf";
    if (/^video\//i.test(mime)) type = "video";
    else if (/^image\//i.test(mime)) type = "image";
    else if (/^audio\//i.test(mime)) type = "audio";
    else if (/document|word/i.test(mime)) type = "drive_doc";
    else if (/spreadsheet/i.test(mime)) type = "drive_sheet";
    else if (/pdf|presentation/i.test(mime)) type = "drive_pdf";
    const url = file.webViewLink || `https://drive.google.com/file/d/${file.id}/view`;
    setNewResource((prev) => ({
      ...prev,
      title: file.name || prev.title,
      url,
      type,
      thumbnail: file.thumbnailLink || prev.thumbnail,
    }));
    setShowDrivePicker(false);
  };

  const allTags = [...new Set(resources.flatMap((r) => r.tags || []))].sort();

  let filtered = resources.filter((r) => {
    const matchSearch =
      !searchQuery ||
      r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.tags || []).some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchType =
      filterType === "all" ||
      r.type === filterType ||
      (filterType === "documentos" && (r.type === "drive_doc" || r.type === "drive_pdf")) ||
      (filterType === "videos" && (r.type === "video" || r.type === "youtube"));
    const matchTag = !filterTag || (r.tags || []).includes(filterTag);
    return matchSearch && matchType && matchTag;
  });

  if (sortBy === "date") filtered = [...filtered].sort((a, b) => (parseResourceDate(b.created_at) || 0) - (parseResourceDate(a.created_at) || 0));
  else if (sortBy === "dateOldest") filtered = [...filtered].sort((a, b) => (parseResourceDate(a.created_at) || 0) - (parseResourceDate(b.created_at) || 0));
  else if (sortBy === "name") filtered = [...filtered].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  else if (sortBy === "type") filtered = [...filtered].sort((a, b) => (a.type || "").localeCompare(b.type || ""));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4 sm:pt-0 pb-4 sm:pb-4" data-testid="shared-resources">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
            <FolderOpen size={24} className="text-white sm:w-7 sm:h-7" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Recursos Compartidos</h1>
            <p className="text-sm sm:text-base text-slate-500">Archivos y enlaces para todo el equipo</p>
          </div>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => {
              setNewResource({ ...initialNewResource });
              setTagsInput("");
              setShowAddModal(true);
            }}
            className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
            data-testid="add-shared-resource-btn"
          >
            <Plus size={20} />
            Agregar Recurso
          </button>
        )}
      </div>

      {/* Search, filters, sort, view mode — en móvil todo ancho y tipo en dropdown */}
      <div className="flex flex-col gap-3 w-full">
        <div className="relative w-full">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar por título, descripción o etiquetas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full glass-input pl-10"
            data-testid="search-resources-input"
          />
        </div>
        {/* Móvil: filtro por tipo como menú desplegable, ancho completo */}
        <div className="sm:hidden w-full">
          <label className="sr-only">Filtrar por tipo</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full glass-input text-sm py-3 pl-4 rounded-xl"
          >
            {TYPE_OPTIONS_FILTER.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
        {/* Etiquetas: solo en móvil, ancho completo */}
        {allTags.length > 0 && (
          <div className="sm:hidden w-full">
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="w-full glass-input text-sm py-3 pl-4 rounded-xl"
            >
              <option value="">Todas las etiquetas</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Desktop: pills de tipo; etiquetas, ordenación y vista en una sola fila */}
        <div className="hidden sm:flex flex-nowrap items-center gap-3 min-w-0">
          <div className="flex gap-2 min-w-0 flex-1 overflow-x-hidden">
            {TYPE_OPTIONS_FILTER.map((t) => (
              <button
                key={t.id}
                onClick={() => setFilterType(t.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                  filterType === t.id ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {allTags.length > 0 && (
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="glass-input text-sm py-2 pl-3 rounded-lg w-[12.5rem] min-w-[12.5rem] shrink-0 max-w-[12.5rem]"
            >
              <option value="">Todas las etiquetas</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-2 ml-auto shrink-0">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="glass-input text-sm py-2 pl-3 rounded-lg"
            >
              <option value="date">Más recientes</option>
              <option value="dateOldest">Más antiguos</option>
              <option value="name">Nombre A-Z</option>
              <option value="type">Tipo</option>
            </select>
            <div className="flex rounded-lg overflow-hidden border border-slate-200">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`p-2 ${viewMode === "grid" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}
                title="Vista grid"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`p-2 ${viewMode === "list" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}
                title="Vista lista"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>
        {/* Móvil: ordenación y vista en una fila, ancho completo */}
        <div className="flex flex-wrap items-center gap-2 w-full sm:hidden">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 min-w-0 glass-input text-sm py-3 pl-4 rounded-xl"
          >
            <option value="date">Más recientes</option>
            <option value="dateOldest">Más antiguos</option>
            <option value="name">Nombre A-Z</option>
            <option value="type">Tipo</option>
          </select>
          <div className="flex rounded-xl overflow-hidden border border-slate-200 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`p-3 ${viewMode === "grid" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}
              title="Vista grid"
            >
              <LayoutGrid size={20} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`p-3 ${viewMode === "list" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}
              title="Vista lista"
            >
              <List size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      {filtered.length > 0 ? (
        <div
          className={
            viewMode === "grid"
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              : "flex flex-col gap-3"
          }
        >
          {filtered.map((resource) => {
            const TypeIcon = getTypeIcon(resource.type);
            const isDrive = isDriveType(resource.type);
            return (
              <div
                key={resource.resource_id}
                onClick={() => {
                  setViewResource(resource);
                  setShowViewModal(true);
                }}
                className={`glass-panel overflow-hidden transition-all hover:shadow-lg cursor-pointer group ${
                  viewMode === "grid" ? "p-0" : "p-4 flex flex-row items-center gap-4"
                }`}
              >
                {/* Thumbnail o icono */}
                <div
                  className={`flex-shrink-0 bg-violet-100 flex items-center justify-center overflow-hidden ${
                    viewMode === "grid"
                      ? "h-36 w-full"
                      : "h-16 w-24 rounded-xl"
                  }`}
                >
                  {(resource.type === "image" && (resource.thumbnail || isDirectImageUrl(resource.url))) ? (
                    <img
                      src={resource.thumbnail || resource.url}
                      alt=""
                      className={viewMode === "grid" ? "w-full h-full object-cover" : "w-full h-full object-cover rounded-xl"}
                      loading="lazy"
                    />
                  ) : resource.thumbnail && (resource.type === "video" || isDrive) ? (
                    <img
                      src={resource.thumbnail}
                      alt=""
                      className={viewMode === "grid" ? "w-full h-full object-cover" : "w-full h-full object-cover rounded-xl"}
                      loading="lazy"
                    />
                  ) : resource.type === "youtube" && extractYoutubeId(resource.url) ? (
                    <img
                      src={`https://img.youtube.com/vi/${extractYoutubeId(resource.url)}/mqdefault.jpg`}
                      alt=""
                      className={viewMode === "grid" ? "w-full h-full object-cover" : "w-full h-full object-cover rounded-xl"}
                      loading="lazy"
                    />
                  ) : resource.type === "video" && isDirectVideoUrl(resource.url) ? (
                    <video
                      src={resource.url}
                      className={viewMode === "grid" ? "w-full h-full object-cover" : "w-full h-full object-cover rounded-xl"}
                      muted
                      preload="metadata"
                      playsInline
                    />
                  ) : (
                    <TypeIcon size={viewMode === "grid" ? 40 : 28} className="text-violet-600" />
                  )}
                </div>
                <div className={viewMode === "grid" ? "p-4" : "flex-1 min-w-0"}>
                  <h3 className="font-semibold text-slate-800 truncate">{resource.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{resource.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(resource.tags || []).slice(0, 3).map((tag, i) => (
                      <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {(parseResourceDate(resource.created_at) || new Date()).toLocaleDateString("es-ES")} · {getTypeLabel(resource.type)}
                    </span>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-primary/10 text-primary rounded-lg"
                        title="Abrir en nueva pestaña"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={16} />
                      </a>
                      {isSuperAdmin && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(resource);
                            }}
                            className="p-2 hover:bg-slate-100 rounded-lg"
                            title="Editar"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(resource);
                            }}
                            className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="glass-panel p-12 text-center">
          <FolderOpen size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-2">
            {searchQuery || filterType !== "all" || filterTag
              ? "No se encontraron recursos con estos criterios"
              : "No hay recursos compartidos aún"}
          </p>
          {!searchQuery && filterType === "all" && !filterTag && (
            <p className="text-sm text-slate-400">
              {isSuperAdmin
                ? "Agrega el primer recurso con el botón de arriba para que todo el equipo pueda verlo aquí."
                : "Solicita a un administrador que agregue recursos para el equipo."}
            </p>
          )}
        </div>
      )}

      {/* Modal Ver recurso */}
      {showViewModal && viewResource && createPortal(
        <div className="modal-overlay fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 py-6">
          <div className="glass-panel w-full max-w-4xl max-h-[calc(100dvh-1.5rem)] flex flex-col animate-slide-up my-auto">
            {/* Solo título y acciones */}
            <div className="flex flex-row items-center gap-2 flex-nowrap shrink-0 p-3 sm:p-4 border-b border-slate-200">
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-bold text-slate-800 truncate">{viewResource.title}</h2>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a
                  href={viewResource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-sm py-2 px-3 min-w-[44px] sm:min-w-0"
                  title="Abrir en nueva pestaña"
                >
                  <ExternalLink size={18} aria-hidden />
                  <span className="hidden sm:inline">Abrir en nueva pestaña</span>
                </a>
                {isSuperAdmin && (
                  <button
                    onClick={() => { setShowViewModal(false); openEdit(viewResource); }}
                    className="btn-secondary inline-flex items-center justify-center gap-2 text-sm py-2 px-3 min-w-[44px] sm:min-w-0"
                    title="Editar"
                  >
                    <Pencil size={18} aria-hidden />
                    <span className="hidden sm:inline">Editar</span>
                  </button>
                )}
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg shrink-0"
                  title="Cerrar"
                  aria-label="Cerrar"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            {/* Contenido scrolleable: recurso + meta + descripción */}
            <div className="flex-1 min-h-0 overflow-y-auto recursos-scrollbar-hide flex flex-col">
              <div className="p-4 bg-slate-50">
              {embedLoading && (
                <div className="flex items-center justify-center h-64">
                  <Loader2 size={32} className="animate-spin text-primary" />
                </div>
              )}
              {!embedLoading && viewResource.type === "audio" && (
                (embedUrl && isDriveUrl(viewResource.url)) ? (
                  <iframe title={viewResource.title} src={embedUrl} className="w-full rounded-lg border-0 min-h-[120px]" />
                ) : (
                  <audio controls src={embedUrl || viewResource.url} className="w-full rounded-lg" />
                )
              )}
              {!embedLoading && embedUrl && viewResource.type === "image" && (
                isDriveUrl(viewResource.url) ? (
                  <iframe title={viewResource.title} src={embedUrl} className="w-full max-h-[70vh] rounded-lg border-0 min-h-[300px]" />
                ) : (
                  <img src={embedUrl} alt={viewResource.title} className="max-w-full h-auto rounded-lg mx-auto" />
                )
              )}
              {!embedLoading && embedUrl && viewResource.type === "video" && isDirectVideoUrl(embedUrl) && (
                <video
                  src={embedUrl}
                  controls
                  className="w-full max-h-[70vh] rounded-lg"
                  playsInline
                />
              )}
              {!embedLoading && embedUrl && viewResource.type === "video" && !isDirectVideoUrl(embedUrl) && (
                <iframe
                  title={viewResource.title}
                  src={embedUrl}
                  className="w-full aspect-video max-h-[70vh] rounded-lg border-0"
                  allowFullScreen
                />
              )}
              {!embedLoading && embedUrl && (isDriveType(viewResource.type) || viewResource.type === "youtube") && (
                <iframe
                  title={viewResource.title}
                  src={embedUrl}
                  className={`w-full rounded-lg border-0 ${viewResource.type === "youtube" ? "aspect-video max-h-[70vh]" : "h-[70vh] min-h-[400px]"}`}
                  allowFullScreen
                />
              )}
              {!embedLoading && !embedUrl && (isDriveType(viewResource.type) || (viewResource.type === "image" && isDriveUrl(viewResource.url)) || (viewResource.type === "audio" && isDriveUrl(viewResource.url))) && (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <p className="text-slate-600 mb-4 max-w-md">
                    No se pudo cargar la vista previa. Ábrelo en una nueva pestaña.
                  </p>
                  <a
                    href={viewResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <ExternalLink size={20} />
                    Abrir en nueva pestaña
                  </a>
                </div>
              )}
              {!embedLoading && !embedUrl && viewResource.type === "video" && (
                <p className="text-slate-500 text-center py-8">
                  No se pudo cargar el vídeo.
                  <a
                    href={viewResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary ml-2 underline"
                  >
                    Abrir enlace
                  </a>
                </p>
              )}
              {!embedLoading && !embedUrl && viewResource.type === "link" && (
                <p className="text-slate-500 text-center py-8">
                  Este recurso debe abrirse en una nueva pestaña.
                  <a
                    href={viewResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary ml-2 underline"
                  >
                    Abrir enlace
                  </a>
                </p>
              )}
              </div>
              {/* Tipo, fecha, etiquetas y descripción debajo del recurso */}
              <div className="p-4 sm:p-5 bg-white border-t border-slate-100 rounded-b-xl">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500 mb-3">
                  <span>{getTypeLabel(viewResource.type)}</span>
                  <span>{(parseResourceDate(viewResource.created_at) || new Date()).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}</span>
                  {(viewResource.tags || []).length > 0 && (
                    <span className="flex flex-wrap gap-1.5 items-center">
                      {(viewResource.tags || []).map((tag) => (
                        <span key={tag} className="inline-flex px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                {viewResource.description && (
                  <div className="text-sm text-slate-600 text-justify whitespace-pre-wrap break-words rounded-lg bg-slate-50/80 p-4">
                    {viewResource.description}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Agregar recurso */}
      {showAddModal && createPortal(
        <div className="modal-overlay fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 py-6 sm:py-8">
          <div className="glass-panel p-5 sm:p-8 w-full max-w-lg max-h-[calc(100dvh-3rem)] overflow-y-auto my-auto animate-slide-up recursos-scrollbar-hide" data-testid="add-shared-resource-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">Agregar Recurso</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de recurso</label>
                {/* Móvil: menú desplegable, ancho completo */}
                <div className="sm:hidden w-full">
                  <select
                    value={newResource.type === "drive_pdf" ? "drive_doc" : newResource.type}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNewResource((prev) => ({ ...prev, type: v }));
                    }}
                    className="w-full glass-input py-3 pl-4 rounded-xl"
                    data-testid="shared-resource-type-select-mobile"
                  >
                    {FORM_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
                {/* Desktop: grid de botones */}
                <div className="hidden sm:grid grid-cols-3 gap-2">
                  {FORM_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = newResource.type === type.id || (type.id === "drive_doc" && newResource.type === "drive_pdf") || (type.id === "video" && newResource.type === "youtube");
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setNewResource((prev) => ({ ...prev, type: type.id }))}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 ${
                          isSelected ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-violet-300"
                        }`}
                      >
                        <Icon size={18} className={isSelected ? "text-violet-600" : "text-slate-400"} />
                        <span className="text-xs text-center leading-tight">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {["drive_doc", "drive_sheet", "drive_pdf", "video", "image", "audio"].includes(newResource.type) && (
                <button
                  type="button"
                  onClick={() => setShowDrivePicker(true)}
                  className="w-full btn-secondary flex items-center justify-center gap-2"
                >
                  <FolderInput size={18} />
                  Elegir desde Google Drive
                </button>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">URL</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    value={newResource.url}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    onPaste={() => setTimeout(handlePasteUrl, 100)}
                    onBlur={handlePasteUrl}
                    className="w-full min-w-0 flex-1 glass-input"
                    placeholder="https://drive.google.com/... o https://youtube.com/..."
                    data-testid="shared-resource-url-input"
                  />
                  <button type="button" onClick={handlePasteUrl} className="btn-secondary whitespace-nowrap w-full sm:w-auto shrink-0" title="Detectar tipo">
                    Detectar
                  </button>
                </div>
                {["drive_doc", "drive_sheet", "drive_pdf", "video"].some((t) => t === newResource.type) && newResource.url && /drive\.google\.com|docs\.google\.com/i.test(newResource.url) && (
                  <p className="mt-2 text-xs text-slate-500">
                    Para que todo el equipo pueda ver este recurso, en Google Drive comparte el archivo como <strong>«Cualquier persona con el enlace puede ver»</strong>.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título *</label>
                <input
                  type="text"
                  value={newResource.title}
                  onChange={(e) => setNewResource((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full glass-input"
                  placeholder="Nombre del recurso"
                  data-testid="shared-resource-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción (opcional)</label>
                <textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full glass-input min-h-[80px]"
                  placeholder="Breve descripción"
                  data-testid="shared-resource-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Etiquetas (opcional)</label>
                {allTags.length > 0 && (
                  <p className="text-xs text-slate-500 mb-2">Selecciona existentes o crea una nueva:</p>
                )}
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {allTags.map((tag) => {
                      const selected = parseTagsString(tagsInput).includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            const current = parseTagsString(tagsInput);
                            if (current.includes(tag)) {
                              setTagsInput(current.filter((t) => t !== tag).join(", "));
                            } else {
                              setTagsInput([...current, tag].join(", "));
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            selected ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = newTagInput.trim();
                        if (t) {
                          const current = parseTagsString(tagsInput);
                          if (!current.includes(t)) setTagsInput([...current, t].join(", "));
                          setNewTagInput("");
                        }
                      }
                    }}
                    className="flex-1 glass-input"
                    placeholder="Nueva etiqueta..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const t = newTagInput.trim();
                      if (t) {
                        const current = parseTagsString(tagsInput);
                        if (!current.includes(t)) setTagsInput([...current, t].join(", "));
                        setNewTagInput("");
                      }
                    }}
                    className="btn-secondary shrink-0"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                {parseTagsString(tagsInput).length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    Seleccionadas: {parseTagsString(tagsInput).join(", ")}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowAddModal(false); setNewTagInput(""); }} className="btn-secondary">
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddResource}
                  disabled={!newResource.title?.trim() || !newResource.url?.trim()}
                  className="btn-primary"
                  data-testid="save-shared-resource-btn"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Editar recurso */}
      {showEditModal && editResource && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto animate-slide-up recursos-scrollbar-hide">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">Editar Recurso</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo</label>
                <select
                  value={editResource.type}
                  onChange={(e) => setEditResource((prev) => ({ ...prev, type: e.target.value }))}
                  className="w-full glass-input pl-3"
                >
                  {RESOURCE_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">URL *</label>
                <input
                  type="url"
                  value={editResource.url}
                  onChange={(e) => setEditResource((prev) => ({ ...prev, url: e.target.value }))}
                  className="w-full glass-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título *</label>
                <input
                  type="text"
                  value={editResource.title}
                  onChange={(e) => setEditResource((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full glass-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  value={editResource.description || ""}
                  onChange={(e) => setEditResource((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full glass-input min-h-[80px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Etiquetas</label>
                {allTags.length > 0 && (
                  <p className="text-xs text-slate-500 mb-2">Selecciona existentes o crea una nueva:</p>
                )}
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {allTags.map((tag) => {
                      const selected = parseTagsString(editTagsInput).includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            const current = parseTagsString(editTagsInput);
                            if (current.includes(tag)) {
                              setEditTagsInput(current.filter((t) => t !== tag).join(", "));
                            } else {
                              setEditTagsInput([...current, tag].join(", "));
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            selected ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editNewTagInput}
                    onChange={(e) => setEditNewTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = editNewTagInput.trim();
                        if (t) {
                          const current = parseTagsString(editTagsInput);
                          if (!current.includes(t)) setEditTagsInput([...current, t].join(", "));
                          setEditNewTagInput("");
                        }
                      }
                    }}
                    className="flex-1 glass-input"
                    placeholder="Nueva etiqueta..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const t = editNewTagInput.trim();
                      if (t) {
                        const current = parseTagsString(editTagsInput);
                        if (!current.includes(t)) setEditTagsInput([...current, t].join(", "));
                        setEditNewTagInput("");
                      }
                    }}
                    className="btn-secondary shrink-0"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                {parseTagsString(editTagsInput).length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    Seleccionadas: {parseTagsString(editTagsInput).join(", ")}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowEditModal(false); setEditNewTagInput(""); }} className="btn-secondary">Cancelar</button>
                <button type="button" onClick={handleEditResource} className="btn-primary">Guardar cambios</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Elegir desde Drive */}
      {showDrivePicker && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-slate-800">Elegir desde Google Drive</h2>
              <button onClick={() => setShowDrivePicker(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 border-b flex gap-2">
              <input
                type="text"
                placeholder="Buscar archivos por nombre..."
                value={driveSearchQuery}
                onChange={(e) => setDriveSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchDriveFiles()}
                className="flex-1 glass-input text-sm"
              />
              <button type="button" onClick={fetchDriveFiles} className="btn-primary text-sm">Buscar</button>
            </div>
            <div className="flex-1 overflow-auto p-4 recursos-scrollbar-hide">
              {driveLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-primary" />
                </div>
              ) : driveFiles.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay archivos o Google Drive no está configurado.</p>
              ) : (
                <ul className="space-y-2">
                  {driveFiles.map((file) => (
                    <li
                      key={file.id}
                      onClick={() => selectDriveFile(file)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-violet-50 cursor-pointer border border-slate-100"
                    >
                      {file.thumbnailLink ? (
                        <img src={file.thumbnailLink} alt="" className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <FileText size={24} className="text-slate-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{file.name}</p>
                        <p className="text-xs text-slate-500">{file.mimeType || ""}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmModal
        open={!!deleteConfirm}
        title="Eliminar recurso"
        message={deleteConfirm ? `¿Eliminar "${deleteConfirm.title}"? Esta acción no se puede deshacer.` : ""}
        onConfirm={() => deleteConfirm && handleDeleteResource(deleteConfirm.resource_id)}
        onClose={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default RecursosCompartidos;
